import { css, apply } from 'twind/css'
import { ThemeType } from './theme-context'

const hexToRgb = (hex: string) => {
  const hexColorChars = 7

  const arrRgb = hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r, g, b) => '#' + r + r + g + g + b + b
    )
    ?.substring(1)
    ?.match(/.{2}/g)
    ?.map((x) => parseInt(x, 16))

  if (hex[0] !== '#' || hex?.length !== hexColorChars || !arrRgb) return hex

  const [r, g, b] = arrRgb
  return `${r}, ${g}, ${b}`
}

const parseTheme = (theme: ThemeType) =>
  Object.entries(theme).reduce((parsedTheme, [themeProp, themeValues]) => {
    const parsedValues = themeValues
      ? Object.entries(themeValues).reduce((values, [key, value]) => {
          const rawColor =
            themeProp === 'colors' && value
              ? {
                  [`--${themeProp}-${key}-raw`]: hexToRgb(value),
                }
              : {}

          return {
            ...values,
            ...rawColor,
            [`--${themeProp}-${key}`]: value,
          }
        }, {})
      : {}

    return {
      ...parsedTheme,
      ...parsedValues,
    }
  }, {})

const defaultColors = {
  'primary-01': '#106EF8',
  'primary-02': '#1063E1',
  'primary-03': '#1055C3',

  'secondary-01': '#3C506B',
  'secondary-02': '#324256',
  'secondary-03': '#202d3e',

  'tertiary-01': '#48B04B',
  'tertiary-02': '#FDA14B',

  'brand-01': '#BFD330',
  'brand-02': '#0F2C84',
  'brand-03': '#0048B0',

  'background-01': '#e5e9ec',
  'background-02': '#f0f2f3',
  'background-03': '#f7f8f9',

  'neutral-01': '#ffffff',
  'neutral-02': '#ced4da',
  'neutral-03': '#e8e8e8',
  'neutral-04': '#002356',
  'neutral-05': '#3c506b',
  'neutral-06': '#6c7c90',
  'neutral-07': '#383838',
  'neutral-08': '#959595',
  'neutral-09': '#11161A',

  'info-01': '#18568e',
  'info-02': '#e2f5fc',

  'error-01': '#ab0707',
  'error-02': '#fee9ec',

  'success-01': '#0c5f1e',
  'success-02': '#E7F5E9',

  'warning-01': '#fbca32',
  'warning-02': '#fef8e2',

  'support-01': '#4840AF',
  'support-02': '#7F81CF',
  'support-03': '#CCCDEC',
  'support-04': '#781064',
  'support-05': '#A95AA1',
  'support-06': '#D4ACD0',
  'support-07': '#C7365D',
  'support-08': '#D86787',
  'support-09': '#EBB3C3',
  'support-10': '#0A735E',
  'support-11': '#48A392',
  'support-12': '#A3D1C8',
  'support-13': '#085960',
  'support-14': '#04A4B0',
  'support-15': '#81D1D7',
  'support-16': '#7A4141',
  'support-17': '#BF6565',
  'support-18': '#FFCAC7',
  'support-19': '#B37A37',
  'support-20': '#C4855A',
  'support-21': '#E1C2AC',
  'support-22': '#606131',
  'support-23': '#84854E',
  'support-24': '#C1C2A7',
  'support-25': '#FEDC97',
  'support-26': '#DFE997',
  'support-27': '#4D596F',
  'support-28': '#85C0F9',
  'support-29': '#C2DFFC',

  'disabled-01': '#CCCCCC',
  'disabled-02': '#878787',
  'disabled-03': '#E8E8E8',
  'disabled-04': '#A7A7A7',
}

const globalStyles = (theme?: ThemeType) => {
  const colors = theme?.colors || {}

  const mergedTheme: ThemeType = {
    colors: {
      ...defaultColors,
      ...colors,
    },
  }

  return css({
    ':global': {
      ':root': {
        ...parseTheme(mergedTheme),
        '--fonts-sans': 'Open Sans, ui-sans-serif, system-ui, sans-serif',
      },

      'html, body': apply`font-sans`,
    },
  })
}

export default globalStyles
